
import { defineComponent, defineAsyncComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import { setI18nLanguage } from '@/i18n'
import { LanguagesType } from '@/types'
import BreadcrumbLayout from '@/components/library/BreadcrumbLayout.vue'
import { RolePermissionDetails } from '@/store/modules/users/interfaces/IUser'
import { getValueByLabel } from '@/utils/permissions'
import { IPortalHardCodeLink } from '@/store/modules/links/interfaces/IPortalHardCodeLink'
import { LinksActionType } from '@/store/modules/links/interfaces'

const AsahiBsHeader = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsHeader.vue')
)

const AsahiBsFooter = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsFooter.vue')
)

const AsahiBsNav = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsNav.vue')
)

export default defineComponent({
  name: 'DefaultLayout',
  components: {
    AsahiBsHeader,
    AsahiBsFooter,
    AsahiBsNav,
    BreadcrumbLayout
  },
  data: () => ({
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType
  }),

  computed: {
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapState('catalog', ['wishlist']),
    ...mapGetters('main', ['getPermissions']),
    ...mapGetters('basket', ['progressBarMessage', 'progressBarSuccess', 'showProgressBarMessage']),
    isCartPage () {
      return this.$route.name === 'cart'
    },
    isDocumentPage () {
      return this.$route.name === 'myDocuments'
    },
    rightMenu () {
      return [
        ...this.checkPermission('My Profile', {
          label: 'My profile',
          href: '/my-profile/details',
          name: 'profile',
          isActive: false

        }),
        ...this.checkPermission('My Documents', {
          label: 'My Documents',
          href: '/my-documents',
          name: 'myDocuments',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Reporting', {
          label: this.$t('Reporting'),
          href: '/reporting',
          name: 'reporting',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Contracts', {
          label: 'Contracts',
          href: '/contracts',
          name: 'contracts',
          isActive: false,
          icon: 'file-contract'
        })
      ]
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    user () {
      return this.$store.state.auth.user
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    favoritesItems () {
      return this.wishlist?.items_v2?.shipto_item_count
    },
    languages () {
      return ['hu', 'en']
    },
    linksStore () {
      return this.$store.state.links
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    cartItems () {
      return this.items?.length
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },
    footerLinksBrands () {
      const brandLinks = this.portalHardCodeLink
        .filter((data) => data.type.toLowerCase() === 'mobile brands sk' || data.type.toLowerCase() === 'mobile brands')
        .map((data) => ({
          label: data.label,
          link: data.url,
          target: '_blank',
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      return [
        {
          title: 'brands'
        },
        ...brandLinks
      ]
    },
    footerCopyrightLinks () {
      const footerLinks = this.portalHardCodeLink
        .filter((data) => data.type.toLowerCase() === 'about us')
        .map((data) => ({
          label: data.label,
          link: data.url,
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      return [
        ...footerLinks
      ]
    },
    isOnOtherPortalPages () {
      return this.$route.name !== 'products' && this.$route.name !== 'homepage' && this.$route.name !== 'favorites'
    }
  },

  methods: {
    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      if (this.customerType) localStorage.setItem('customertype', this.customerType?.customerType)

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },
    async removeProductFromCart (productId: string) {
      if (this.$store.state.basket?.id && productId) {
        await this.$store.dispatch('basket/REMOVE_ITEM', {
          cart_id: this.$store.state.basket.id,
          cart_item_id: productId
        }, { root: true })
        await this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.$store.state.basket.id }, { root: true })
      }
    },
    fetchPortalPageMenuLink () {
      const customertype = localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_PAGE_MENU_LINK,
        { customertype, uid }
      )
    },

    fetchLinks () {
      const customertype = localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_ALL_LINKS,
        { customertype, uid }
      )
    },

    fetchPortalHardCodeLink () {
      const customertype = localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_PORTAL_HARD_CODE_LINK,
        { customertype, uid }
      )
    }
  },
  mounted () {
    if (this.isOnOtherPortalPages) {
      this.$store.dispatch('catalog/FETCH_WISHLIST', { bypassloading: true }, { root: true })
    }
  },
  watch: {
    activeLanguage (newLang, oldLang) {
      if (newLang !== oldLang) {
        this.fetchPortalPageMenuLink()
        this.fetchLinks()
        this.fetchLinks()
      }
    }
  }
})
